import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { ENV } from '@/config/env'

// Live "something changed" signals from blockpot-service (BLO-974/BLO-975):
// one server-sent-events stream per chain carries every log the Blockpot and
// draw contracts emit. Messages are invalidation signals, not state: hooks
// refetch with eth_call when a matching event arrives. Unconfirmed logs are
// pushed too, so never treat a message as final.

export type StreamContract =
    | 'operator'
    | 'draw'
    | 'quickGame'
    | 'kycRegistry'
    | 'playerRegistry'
    | 'operatorRoles'
    | 'referralManager'
    | 'vrfProvider'

// The `data:` payload of every `chain` event. `args` holds indexed args only,
// as strings: addresses lowercase 0x hex, numbers decimal.
export type ChainEventMessage = {
    chainId: number
    contract: StreamContract
    address: `0x${string}`
    event: string
    args: Record<string, string>
    blockNumber: number
    logIndex: number
    txHash: `0x${string}`
}

type Subscriber = {
    onEvent: (message: ChainEventMessage) => void
    onReset: () => void
}

type Connection = {
    source: EventSource | null
    subscribers: Set<Subscriber>
    connected: boolean
    statusListeners: Set<() => void>
    retryTimer: ReturnType<typeof setTimeout> | undefined
}

// The service answers 503 with Retry-After: 10 when it is over its connection
// caps. EventSource gives up on a non-200, so reconnect by hand after that.
const RECONNECT_DELAY_MS = 10_000

const connections = new Map<number, Connection>()

export function eventStreamUrl(chainId: number): string {
    return `${ENV.GAMING_SERVICE_URL.replace(/\/$/, '')}/v1/events/stream?chainId=${chainId}`
}

function isRecordOfStrings(value: unknown): value is Record<string, string> {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
    return Object.values(value).every((v) => typeof v === 'string')
}

export function parseChainEvent(chainId: number, data: string): ChainEventMessage | null {
    let parsed: unknown
    try {
        parsed = JSON.parse(data)
    } catch {
        return null
    }
    if (typeof parsed !== 'object' || parsed === null) return null
    const m = parsed as Record<string, unknown>
    if (m.chainId !== chainId) return null
    if (typeof m.contract !== 'string' || typeof m.event !== 'string') return null
    if (typeof m.address !== 'string' || typeof m.txHash !== 'string') return null
    if (typeof m.blockNumber !== 'number' || typeof m.logIndex !== 'number') return null
    if (!isRecordOfStrings(m.args)) return null
    return parsed as ChainEventMessage
}

function setConnected(conn: Connection, connected: boolean) {
    if (conn.connected === connected) return
    conn.connected = connected
    for (const listener of conn.statusListeners) listener()
}

function open(chainId: number, conn: Connection, afterGap: boolean) {
    if (typeof EventSource === 'undefined') return
    const source = new EventSource(eventStreamUrl(chainId))
    conn.source = source
    source.addEventListener('open', () => {
        setConnected(conn, true)
        // A fresh EventSource after a hard failure has no Last-Event-ID, so
        // events may have been missed: treat it like a reset.
        if (afterGap) {
            afterGap = false
            for (const s of conn.subscribers) s.onReset()
        }
    })
    source.addEventListener('error', () => {
        setConnected(conn, false)
        if (source.readyState !== EventSource.CLOSED) return
        conn.source = null
        conn.retryTimer = setTimeout(() => {
            conn.retryTimer = undefined
            if (conn.subscribers.size > 0) open(chainId, conn, true)
        }, RECONNECT_DELAY_MS)
    })
    source.addEventListener('chain', (event) => {
        const message = parseChainEvent(chainId, (event as MessageEvent<string>).data)
        if (!message) return
        for (const s of conn.subscribers) s.onEvent(message)
    })
    source.addEventListener('reset', () => {
        for (const s of conn.subscribers) s.onReset()
    })
}

function connection(chainId: number): Connection {
    let conn = connections.get(chainId)
    if (!conn) {
        conn = {
            source: null,
            subscribers: new Set(),
            connected: false,
            statusListeners: new Set(),
            retryTimer: undefined,
        }
        connections.set(chainId, conn)
    }
    return conn
}

function subscribe(chainId: number, subscriber: Subscriber): () => void {
    const conn = connection(chainId)
    conn.subscribers.add(subscriber)
    if (!conn.source && !conn.retryTimer) open(chainId, conn, false)
    return () => {
        conn.subscribers.delete(subscriber)
        if (conn.subscribers.size > 0) return
        conn.source?.close()
        conn.source = null
        if (conn.retryTimer) clearTimeout(conn.retryTimer)
        conn.retryTimer = undefined
        setConnected(conn, false)
        if (conn.statusListeners.size === 0) connections.delete(chainId)
    }
}

function subscribeStatus(chainId: number, listener: () => void): () => void {
    const conn = connection(chainId)
    conn.statusListeners.add(listener)
    return () => {
        conn.statusListeners.delete(listener)
        if (conn.statusListeners.size === 0 && conn.subscribers.size === 0) {
            connections.delete(chainId)
        }
    }
}

// Whether the chain's stream is currently open. While it is down, hooks fall
// back to a coarse staleTime so data still refreshes.
export function useEventStreamConnected(chainId: number): boolean {
    const sub = useCallback((listener: () => void) => subscribeStatus(chainId, listener), [chainId])
    const get = useCallback(() => connections.get(chainId)?.connected ?? false, [chainId])
    return useSyncExternalStore(sub, get, () => false)
}

// Subscribes to the chain's service event stream. Every caller on a chain
// shares one EventSource, closed when the last subscriber unmounts.
export function useServiceEventStream(
    chainId: number,
    onEvent: (message: ChainEventMessage) => void,
    onReset?: () => void,
    enabled = true,
): { connected: boolean } {
    const onEventRef = useRef(onEvent)
    const onResetRef = useRef(onReset)
    useEffect(() => {
        onEventRef.current = onEvent
        onResetRef.current = onReset
    })

    useEffect(() => {
        if (!enabled) return
        return subscribe(chainId, {
            onEvent: (m) => onEventRef.current(m),
            onReset: () => onResetRef.current?.(),
        })
    }, [chainId, enabled])

    return { connected: useEventStreamConnected(chainId) }
}

// Whether `message` is `event` from `contract`, with every given indexed arg
// equal (case-insensitive, so checksummed addresses match).
export function matchesEvent(
    message: ChainEventMessage,
    contract: StreamContract,
    event: string,
    args: Record<string, string | undefined> = {},
): boolean {
    if (message.contract !== contract || message.event !== event) return false
    for (const [key, value] of Object.entries(args)) {
        if (value === undefined) continue
        if (message.args[key]?.toLowerCase() !== value.toLowerCase()) return false
    }
    return true
}
